import { createSlice } from "@reduxjs/toolkit";

import {
  executeListReferenceLines,
  executeUploadReferenceLine,
} from "../api/ReferenceLineApi";
import { storeKeyName } from "../common/AppConstant";
import { handleError } from "../common/ErrorUtil";
import {
  curveCsvColumn,
  curveCsvName,
  curveInfo,
  textDelimiter,
  textEndType,
} from "../model/Common";

import {
  fetchChangeSortedOfficeInfos,
  fetchListOfficeInfos,
} from "./OfficeStore";

/* 凡例入力データ(初期値)作成 */
const legendDataDefault = () => {
  const result = [];
  const groups = Object.keys(curveInfo);

  groups.forEach((group) => {
    const lines = Object.keys(curveInfo[group]);

    lines.forEach((line) => {
      result.push({
        type: curveInfo[group][line].key,
        name: curveInfo[group][line].legend,
        disabled: true,
        error: false,
      });
    });
  });
  return result;
};

/**
 * Slice.
 *
 * @type {object}
 */
export const slice = createSlice({
  // Slice名
  name: storeKeyName.referenceLine,

  // 初期State
  initialState: {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: false,
    // エラー内容
    error: null,
    // グラフ線図情報(DB取得)
    referenceLines: [],
    // 凡例名称情報
    legendData: legendDataDefault(),
    // グラフ線図登録CSVデータ
    csvData: [],
  },

  // Reducer
  reducers: {
    // 通信開始
    fetchStart(state) {
      state.isLoading = true;
    },
    // 通信失敗
    fetchFailure(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
    // 通信成功
    fetchSuccess(state, action) {
      state.error = null;
      state.referenceLines = action.payload;
      state.legendData = legendDataDefault();
      state.csvData = [];

      // グラフ線図が登録済みの場合、凡例名称初期値を更新
      state.referenceLines.forEach((line) => {
        const index = state.legendData.findIndex(
          (item) => item.type === line.type
        );
        if (index >= 0) {
          state.legendData[index].name = line.name;
          state.legendData[index].disabled = false;
        }
      });
      state.isLoading = false;
    },
    // クリア
    fetchClear(state) {
      state.error = null;
      state.referenceLines = [];
      state.legendData = legendDataDefault();
      state.csvData = [];
      state.isLoading = false;
    },
    // 凡例名称情報更新
    fetchUpdateLegendData(state, action) {
      const { type, name } = action.payload;
      const index = state.legendData.findIndex((item) => item.type === type);
      if (index >= 0) {
        state.legendData[index].name = name;
        if (name !== "") {
          state.legendData[index].error = false;
        } else {
          // 入力文字列が空文字の場合、エラー状態に遷移
          state.legendData[index].error = true;
        }
      }
    },
    // CSVデータ更新
    fetchUpdateCsvData(state, action) {
      const legendData = [];
      let disabled;
      let target;
      if (action.payload) {
        state.csvData = action.payload;
      } else {
        state.csvData = [];
      }

      state.legendData.forEach((data) => {
        disabled = true;
        if (action.payload) {
          // CSVに含まれる線種の凡例入力フォームを有効化
          target = state.csvData.find((item) => item.type === data.type);
        } else {
          // CSV未選択の場合、DBから取得できた線種の凡例入力フォームを有効化
          target = state.referenceLines.find((item) => item.type === data.type);
        }

        if (target) {
          disabled = false;
        }

        legendData.push({ ...data, disabled });
      });
      state.legendData = legendData;
    },
  },
});

/**
 * Actions.
 *
 * @type {object}
 */
export const {
  fetchStart,
  fetchFailure,
  fetchSuccess,
  fetchClear,
  fetchUpdateLegendData,
  fetchUpdateCsvData,
} = slice.actions;

/**
 * Selectors.
 *
 * @type {function}
 */
export const selector = (stateAll) =>
  // すべてのStoreのStateが含まれているため、特定のStoreのStateのみ返す
  stateAll[storeKeyName.referenceLine];

/**
 * fetchListReferenceLines.
 *
 */
export const fetchListReferenceLines = (serialNumber) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListReferenceLines(serialNumber).catch(
    handleError
  );
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchSuccess(data));
};

/**
 * fetchClearDialog.
 *
 */
export const fetchClearDialog = () => async (dispatch) => {
  dispatch(fetchClear());
};

/**
 * fetchChangeLegendData.
 *
 */
export const fetchChangeLegendData = (type, name) => async (dispatch) => {
  dispatch(fetchUpdateLegendData({ type, name }));
};

/**
 * fetchUploadReferenceLine.
 *
 */
export const fetchUploadReferenceLine = (
  serialNumber,
  csvData,
  legendData,
  sortState
) => async (dispatch) => {
  // CSVデータと凡例入力フォームの情報を結合
  const info = [];
  legendData.forEach((legend) => {
    let result;
    if (!legend.disabled) {
      const data = csvData.find((csv) => csv.type === legend.type);

      if (data) {
        result = {
          type: legend.type,
          name: legend.name,
          value: data.value,
        };
      } else {
        result = {
          type: legend.type,
          name: legend.name,
          value: [],
        };
      }
      info.push(result);
    }
  });

  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { error } = await executeUploadReferenceLine(serialNumber, info).catch(
    handleError
  );
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchSuccess([]));

  // 工番一覧情報を更新
  dispatch(fetchListOfficeInfos());
  dispatch(fetchChangeSortedOfficeInfos(sortState));
};

/**
 * fetchReadCsv.
 *
 */
export const fetchReadCsv = (serialNumber, file) => async (dispatch) => {
  // CSV読み込み
  let result;
  if (file && file.path === curveCsvName(serialNumber)) {
    const reader = new FileReader();
    reader.readAsText(file);
    await new Promise((resolve) => {
      reader.onload = () => resolve();
    });
    const csvData = reader.result;

    // 改行コード判定
    let textEnd = textEndType.crlf;
    Object.keys(textEndType).some((type) => {
      if (csvData.includes(textEndType[type])) {
        textEnd = textEndType[type];
        return true;
      }
      return false;
    });

    // 改行コードで行単位に配列変換(先頭の見出し行は削除)
    const rows = csvData.split(textEnd);
    rows.shift();
    result = [];
    rows.forEach((row) => {
      // 区切り文字でセル単位に配列変換
      const cells = row.split(textDelimiter);
      const type = cells[curveCsvColumn.type];
      const x = parseFloat(cells[curveCsvColumn.x]);
      const y = parseFloat(cells[curveCsvColumn.y]);

      if (type !== "") {
        const index = result.findIndex((data) => data.type === type);
        if (index >= 0) {
          // 同種のデータが存在する場合、座標データを追加
          result[index].value.push([x, y]);
        } else {
          // 同種のデータが存在しない場合、新規追加
          result.push({ type, name: "", value: [[x, y]] });
        }
      }
    });
  }

  dispatch(fetchUpdateCsvData(result));
};
